import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Alert, Box, Button, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useNotification } from '../../context/NotificationContext';
import { ReactComponent as Logo } from '../../logo.svg';
import { authService } from '../../services/api/authService';

const RootContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #181e25;
  padding: 1rem;
  @media (min-width: 600px) {
    padding: 2rem;
  }
`;

const BrandingBox = styled(Box)`
  background: #377dff;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 24px 0 0 24px;
  @media (max-width: 900px) {
    border-radius: 24px 24px 0 0;
    min-height: 150px;
    padding: 24px 0;
  }
  @media (max-width: 600px) {
    min-height: 120px;
    padding: 16px 0;
  }
`;

const FormBox = styled(Box)`
  background: #232933;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 32px;
  border-radius: 0 24px 24px 0;
  @media (max-width: 900px) {
    border-radius: 0 0 24px 24px;
    padding: 32px 16px;
  }
  @media (max-width: 600px) {
    padding: 24px 16px;
  }
`;

const Card = styled(Box)`
  width: 100%;
  max-width: 1100px;
  border-radius: 24px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  overflow: hidden;
  background: transparent;
`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token || !userId) {
      setError('Invalid reset link');
      return;
    }

    try {
      setLoading(true);
      await authService.resetPassword(token, userId, password);
      showNotification('Password reset successful', 'success');
      navigate('/login');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <RootContainer>
      <Card>
        <Grid container sx={{ minHeight: { xs: 'auto', md: 600 } }}>
          <Grid size={{xs:12, md:6}}>
            <BrandingBox>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Logo style={{ width: 'auto', height: 'auto', maxWidth: '200px', maxHeight: '60px' }} />
              </Box>
            </BrandingBox>
          </Grid>
          <Grid size={{xs:12, md:6}}>
            <FormBox>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1, textAlign: 'center', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                Reset Password
              </Typography>
              <Box sx={{ mb: 3, color: '#b0b8c1', textAlign: 'center', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Enter your new password below
              </Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  inputProps={{ minLength: 8 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{ color: '#b0b8c1' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  inputProps={{ minLength: 8 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{ color: '#b0b8c1' }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, background: '#2563EB', color: '#fff', fontWeight: 700, borderRadius: 2, '&:hover': { background: '#1d4ed8' } }}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Box>
            </FormBox>
          </Grid>
        </Grid>
      </Card>
    </RootContainer>
  );
} 